import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import SEO from "../../components/common/SEO";
import { generateBreadcrumbSchema } from "../../utils/seo/structuredData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faClock,
  faSearch,
  faFistRaised,
  faChevronRight,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useFetchScheduledEvents } from "../../hooks/events/useFetchScheduledEvents";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { setEventId, setEventName } from "../../features/event/eventReducer";
import {
  getEventDisplayType,
  getUpcomingBadgeText,
  hasEventPassed,
} from "../../utils/eventTimingHelper";
import CountdownTimer from "../../components/common/CountdownTimer";
import {
  resetAlternateLotn,
  resetAlternateSelections,
  resetLineup,
  resetLotn,
  resetProjectedTotal,
} from "../../features/lineup/lineupReducer";
import Loading from "../../components/common/Loading";

const EventsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredEvents, setFilteredEvents] = useState([]);

  const { isLoading: isUserLoading, error: userError } = useFetchUser();
  const {
    data: eventsData,
    isLoading: isEventsLoading,
    error: eventsError,
  } = useFetchScheduledEvents();

  // Filter events based on search query
  useEffect(() => {
    if (eventsData) {
      if (!searchQuery) {
        setFilteredEvents(eventsData);
      } else {
        const query = searchQuery.toLowerCase();
        setFilteredEvents(
          eventsData.filter(
            (event) =>
              event.Name.toLowerCase().includes(query) ||
              event.ShortName.toLowerCase().includes(query),
          ),
        );
      }
    }
  }, [searchQuery, eventsData]);

  const handleEventSelect = (eventId, eventName) => {
    // Reset lineup data when selecting a new event
    dispatch(resetLineup());
    dispatch(resetAlternateSelections());
    dispatch(resetLotn());
    dispatch(resetAlternateLotn());
    dispatch(resetProjectedTotal());

    // Set the current event in Redux
    dispatch(setEventId(eventId));
    dispatch(setEventName(eventName));

    // Navigate to event details
    navigate(`/events/${eventId}`);
  };

  const isLoading = isUserLoading || isEventsLoading;
  const hasError = userError || eventsError;

  if (isLoading) return <Loading />;

  if (hasError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-deep-red w-8 h-8"
            />
          </div>
          <h3 className="text-xl font-bold text-dark-grey mb-2">
            Unable to Load Events
          </h3>
          <p className="text-medium-grey mb-4">
            {userError?.message ||
              eventsError?.message ||
              "Could not load event data."}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-deep-red hover:bg-bright-red text-white px-6 py-3 rounded-xl font-bold transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Split into upcoming and past events (past = before Sunday 8pm cutoff)
  const upcomingEvents =
    filteredEvents?.filter((event) => !hasEventPassed(event.DateTime)) || [];
  const pastEvents =
    filteredEvents?.filter((event) => hasEventPassed(event.DateTime)) || [];

  // Sort events by date (nearest first for upcoming, most recent first for past)
  upcomingEvents.sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));
  pastEvents.sort((a, b) => new Date(b.DateTime) - new Date(a.DateTime));

  const getEventUrgency = (dateTime) => {
    const eventDate = new Date(dateTime);
    const now = new Date();
    const hoursUntil = (eventDate - now) / (1000 * 60 * 60);

    if (hoursUntil <= 24 && hoursUntil > 0) return "urgent";
    if (hoursUntil <= 72 && hoursUntil > 0) return "soon";
    return "normal";
  };

  const renderEventCard = (event, isPast = false) => {
    const eventDate = new Date(event.DateTime);
    const today = new Date();
    const fightsCount = event.Fights?.length || null;
    const urgency = getEventUrgency(event.DateTime);
    const isToday = today.toDateString() === eventDate.toDateString();
    const isTomorrow =
      new Date(today.getTime() + 24 * 60 * 60 * 1000).toDateString() ===
      eventDate.toDateString();

    // Get display type for countdown vs upcoming badge
    const displayType = !isPast ? getEventDisplayType(event.DateTime) : "none";
    const showCountdown = displayType === "countdown";
    const showUpcomingBadge = displayType === "upcoming";

    const formatDate = () => {
      if (isToday) return "Today";
      if (isTomorrow) return "Tomorrow";
      return eventDate.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      });
    };

    return (
      <div
        key={event._id || event.EventId}
        className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-200 ${
          isPast ? "opacity-75" : ""
        } ${
          urgency === "urgent"
            ? "ring-2 ring-red-200"
            : urgency === "soon"
              ? "ring-1 ring-yellow-200"
              : ""
        }`}
        onClick={() => handleEventSelect(event.EventId, event.Name)}
      >
        {/* Countdown Timer Banner */}
        {showCountdown && (
          <div className="bg-deep-red text-white px-4 py-2 text-xs font-bold flex items-center justify-center gap-2">
            <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
            <CountdownTimer
              targetDate={event.DateTime}
              className="bg-transparent text-white p-0 animate-pulse"
              showIcon={false}
            />
          </div>
        )}

        {/* Upcoming Banner */}
        {showUpcomingBadge && (
          <div className="bg-yellow-600 text-white px-4 py-2 text-xs font-bold text-center">
            <FontAwesomeIcon icon={faClock} className="w-3 h-3 mr-1" />
            {getUpcomingBadgeText(event.DateTime)}
          </div>
        )}

        {/* Urgency Banner (for other events) */}
        {!showCountdown &&
          !showUpcomingBadge &&
          urgency !== "normal" &&
          !isPast && (
            <div
              className={`px-4 py-2 text-xs font-bold text-white ${
                urgency === "urgent" ? "bg-red-600" : "bg-yellow-600"
              }`}
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="w-3 h-3 mr-1"
              />
              {urgency === "urgent" ? "STARTING SOON" : "UPCOMING"}
            </div>
          )}

        <div className="p-5">
          <div className="flex items-start gap-4">
            {/* Date Card */}
            <div className="flex-shrink-0 w-14 h-16 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
              <div className="bg-deep-red text-white text-center py-1 text-xs font-bold uppercase">
                {eventDate.toLocaleString("default", { month: "short" })}
              </div>
              <div className="bg-white text-center flex items-center justify-center h-11 font-bold text-lg text-dark-grey">
                {eventDate.getDate()}
              </div>
            </div>

            {/* Event Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="text-sm font-bold text-deep-red mb-1">
                    {event.ShortName}
                  </div>
                  <h3 className="text-lg font-bold text-dark-grey leading-tight line-clamp-2">
                    {event.Name}
                  </h3>
                </div>
              </div>

              {/* Event Meta */}
              <div className="flex items-center gap-4 text-sm text-medium-grey mb-3">
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-deep-red w-3 h-3"
                  />
                  <span className="font-medium">{formatDate()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon
                    icon={faClock}
                    className="text-deep-red w-3 h-3"
                  />
                  <span className="font-medium">
                    {eventDate.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>

              {/* Fight Count & Action */}
              <div className="flex items-center justify-between">
                {fightsCount && (
                  <div className="flex items-center gap-1 text-sm text-medium-grey">
                    <FontAwesomeIcon
                      icon={faFistRaised}
                      className="text-deep-red w-3 h-3"
                    />
                    <span className="font-medium">{fightsCount} fights</span>
                  </div>
                )}

                <div className="flex items-center gap-1 text-sm font-bold text-deep-red">
                  <span>View Details</span>
                  <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = (title, description, icon) => (
    <div className="bg-gray-50 rounded-xl p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <FontAwesomeIcon icon={icon} className="text-gray-400 w-8 h-8" />
      </div>
      <h3 className="text-lg font-bold text-dark-grey mb-2">{title}</h3>
      <p className="text-medium-grey">{description}</p>
    </div>
  );

  const breadcrumbs = [
    { name: "Home", path: "/home" },
    { name: "Events", path: "/events" },
  ];

  return (
    <div className="space-y-6">
      <SEO
        title="UFC Events & Fight Cards | DayShift MMA Fantasy League"
        description="Browse upcoming and past UFC events. Build your fantasy lineup, track fight cards, and compete in fantasy MMA leagues. Stay updated with the latest UFC schedule."
        keywords="UFC events, UFC fight card, upcoming UFC fights, UFC schedule, MMA events, fantasy MMA lineup, UFC fantasy picks"
        url="/events"
        structuredData={generateBreadcrumbSchema(breadcrumbs)}
      />

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-deep-red rounded-xl flex items-center justify-center">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="text-white w-6 h-6"
              />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-dark-grey">
                MMA Events
              </h1>
              <p className="text-medium-grey text-sm">
                Discover and track upcoming UFC fights
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-sm">
            <FontAwesomeIcon
              icon={faSearch}
              className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-gray-50 border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-gray-700 focus:outline-none focus:border-deep-red focus:bg-white transition-colors w-full"
            />
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FontAwesomeIcon
            icon={faInfoCircle}
            className="text-blue-600 w-5 h-5 mt-0.5"
          />
          <div>
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Pro Tip:</span> Click on any event
              to view fight details and create your lineup. Only events that are
              part of your leagues will show lineup submission options.
            </p>
          </div>
        </div>
      </div>

      {/* Upcoming Events */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-dark-grey">Upcoming Events</h2>
          {upcomingEvents.length > 0 && (
            <span className="px-3 py-1 bg-deep-red/10 text-deep-red rounded-full text-sm font-bold">
              {upcomingEvents.length} events
            </span>
          )}
        </div>

        {upcomingEvents.length === 0 ? (
          renderEmptyState(
            "No Upcoming Events",
            "Check back soon for new events or explore past events below.",
            faCalendarAlt,
          )
        ) : (
          <div className="grid gap-4 md:gap-6">
            {upcomingEvents.map((event) => renderEventCard(event, false))}
          </div>
        )}
      </div>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-dark-grey">Past Events</h2>
            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-bold">
              {pastEvents.length} events
            </span>
          </div>

          <div className="grid gap-4 md:gap-6">
            {pastEvents
              .slice(0, 10)
              .map((event) => renderEventCard(event, true))}
          </div>

          {pastEvents.length > 10 && (
            <div className="text-center">
              <p className="text-medium-grey text-sm">
                Showing 10 of {pastEvents.length} past events
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventsPage;
